// src/services/expenseApi.js
// Servicio para gastos según documentación del backend:
// GET /expense → lista | GET /expense/{id} → detalle
// GET /expense/expense-report → por meses | expense-report-day → por día | expense-report-category → por categoría
// POST /expense → crear | PUT /expense/{id} → actualizar | DELETE /expense/{id} → eliminar

import { dotoniwoofApi } from '../api/dotoniwooApi';

const EXPENSE_BASE = '/expense.php';

/**
 * Lista todos los gastos
 * @returns {Promise<Object>} { ok, status, data }
 */
export const getExpenses = async () => {
  try {
    const response = await dotoniwoofApi.get(EXPENSE_BASE);
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener gastos:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Obtiene un gasto por ID
 * @param {number|string} id - ID del gasto
 * @returns {Promise<Object>}
 */
export const getExpenseById = async (id) => {
  try {
    const response = await dotoniwoofApi.get(`${EXPENSE_BASE}/${id}`);
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener gasto:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Reporte de gastos por meses
 * @param {number} [year] - Año (opcional)
 * @returns {Promise<Object>}
 */
export const getExpenseReport = async (year) => {
  try {
    const url = year ? `${EXPENSE_BASE}/expense-report?year=${year}` : `${EXPENSE_BASE}/expense-report`;
    const response = await dotoniwoofApi.get(url);
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener reporte de gastos por mes:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Reporte de gastos por día
 * @param {number|string} month - Mes
 * @param {number|string} year - Año
 * @returns {Promise<Object>}
 */
export const getExpenseReportDay = async (month, year) => {
  try {
    const response = await dotoniwoofApi.get(
      `${EXPENSE_BASE}/expense-report-day?month=${month}&year=${year}`
    );
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener reporte de gastos por día:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Reporte de gastos por categoría
 * @param {number|string} [month] - Mes (opcional)
 * @returns {Promise<Object>}
 */
export const getExpenseReportCategory = async (month) => {
  try {
    const url = month != null ? `${EXPENSE_BASE}/expense-report-category?month=${month}` : `${EXPENSE_BASE}/expense-report-category`;
    const response = await dotoniwoofApi.get(url);
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al obtener reporte de gastos por categoría:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Crea un gasto (incluye id_employee cuando aplica, ej. Sueldos/Comisiones)
 * @param {Object} payload - { firebase_UID, id_category, description, amount, expense_date [, id_employee] }
 * @returns {Promise<Object>}
 */
export const createExpense = async (payload) => {
  try {
    const response = await dotoniwoofApi.post(EXPENSE_BASE, payload);
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al crear gasto:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Actualiza un gasto
 * @param {number|string} id - ID del gasto
 * @param {Object} payload - Campos a actualizar
 * @returns {Promise<Object>}
 */
export const updateExpense = async (id, payload) => {
  try {
    const response = await dotoniwoofApi.put(`${EXPENSE_BASE}/${id}`, payload);
    return { ok: true, status: response.status, data: response.data };
  } catch (error) {
    console.error('Error al actualizar gasto:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};

/**
 * Elimina un gasto (el backend requiere firebase_UID)
 * @param {number|string} id - ID del gasto
 * @param {string} firebase_UID - UID de Firebase del usuario
 * @returns {Promise<Object>}
 */
export const deleteExpense = async (id, firebase_UID) => {
  try {
    const response = await dotoniwoofApi.delete(`${EXPENSE_BASE}/${id}`, {
      data: { firebase_UID },
      params: { firebase_UID }
    });
    return {
      ok: true,
      status: response.status,
      data: response.data
    };
  } catch (error) {
    console.error('Error al eliminar gasto:', error);
    return {
      ok: false,
      status: error.response?.status || 500,
      data: error.response?.data || { message: 'Error de conexión' }
    };
  }
};
